// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { toast } from "react-toastify";
import actionCreators from "@modules/shared/actionCreator";
import config from "@config";
import { transformError } from "@transformers/response";
import {
  GetEmployeeEventHistory,
  GetManualReferralEnabled,
  PostManualReferral,
  GetReferralProcesses,
  GetEmployeeDocuments,
  GetEmployeeSpecificDocuments,
  RemoveEmployeeDocument,
  AddEmployeeDocument,
  DownloadEmployeeDocument,
  AddEmployeeAppeal,
  UpdateEmployeeAppeal,
  ResolveEmployeeAppeal,
  EnterAbsenceMonitoringProcess,
} from "@modules/shared/constants/actions";
import employeeActionCreators from "@modules/employee/actionCreator";
import { transformFilters } from "@transformers/request";
import { sendSuccessMessage, sendErrorMessage } from "@modules/app/actionCreator";
import { dataURLtoFile } from "@utils/file";
import { downloadFile } from "@sagas/utils";

type EmployeeEventsListRequest = $Exact<{
  clientId: string,
  employeeId: string,
  page?: number,
}>;

type EventHistoryListRequest = $Exact<{
  filters?: Object,
  limit?: number,
  ...EmployeeEventsListRequest,
}>;

export function* onGetEmployeeEventHistory({
  payload,
}: Action<EventHistoryListRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const limit = payload.limit || config.results100PerPage;
    const taskType = (payload.filters || {}).taskType || "ALL";
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/activity-history`,
      {
        params: {
          limit,
          search: transformFilters(Object.assign({}, payload.filters)),
          page: payload.page,
        },
      },
    );

    yield put(
      actionCreators.GetEmployeeEventHistorySuccess({
        data: response.data,
        taskType,
      }),
    );
  } catch (e) {
    yield put(actionCreators.GetEmployeeEventHistoryError(transformError(e)));
  }
}
export function* onGetManualReferralEnabled({
  payload,
}: Action<EventHistoryListRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manual-referral/enabled`,
    );

    yield put(actionCreators.GetManualReferralEnabledSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetManualReferralEnabledError(transformError(e)));
  }
}
export function* onPostManualReferral({
  payload,
}: Action<{ ...EventHistoryListRequest, formData: Object }>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, employeeId, formData } = payload;

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${clientId}/employee/${employeeId}/manual-referral`,
      formData,
    );

    yield all([
      put(
        actionCreators.GetManualReferralEnabled({
          clientId,
          employeeId,
        }),
      ),
      put(
        actionCreators.GetEmployeeEventHistory({
          clientId,
          employeeId,
        }),
      ),
      put(
        employeeActionCreators.GetEmployeeReferralHistory({
          clientId,
          employeeId,
        }),
      ),
      put(actionCreators.ToggleManualReferralModal()),
    ]);
  } catch (e) {
    yield yield all([
      put(actionCreators.ToggleManualReferralModal()),
      put(toast.error(e.response.data.messages[0] || JSON.stringify(e.response.data.messages))),
    ]);
  }
}
export function* onGetReferralProcesses({
  payload,
}: Action<EventHistoryListRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/referral/process`,
    );

    yield put(actionCreators.GetReferralProcessesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReferralProcessesError(transformError(e)));
  }
}

export function* onGetEmployeeDocuments({
  payload,
}: Action<EmployeeEventsListRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const page = payload.page || 1;

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/file`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
          sort: "created_at,desc",
        },
      },
    );

    yield put(actionCreators.GetEmployeeDocumentsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeDocumentsError(transformError(e)));
  }
}

type DocumentRequest = $Exact<{
  ...EmployeeEventsListRequest,
  documentId: number,
  documentName: string,
}>;

export function* onRemoveEmployeeDocument({
  payload,
}: Action<DocumentRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.documentId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/file/${payload.documentId}`,
    );

    yield all([
      put(actionCreators.ToggleRemoveEmployeeDocumentModal({ visible: false })),
      put(
        actionCreators.GetEmployeeDocuments({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage("DOCUMENT_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveEmployeeDocumentError(transformError(e)));
  }
}

type AddDocumentRequest = $Exact<{
  ...EmployeeEventsListRequest,
  formData: Object,
}>;

export function* onAddEmployeeDocument({
  payload,
}: Action<AddDocumentRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const file = dataURLtoFile(payload.formData.file);

    const fileType = payload.formData.file_type;

    // Preparing "multipart/form-data" request
    const data = new FormData();
    data.append("file", file.blob, decodeURIComponent(file.name));
    data.append("fileType", fileType);

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/file`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    yield all([
      put(actionCreators.ToggleAddEmployeeDocumentModal(false)),
      put(
        actionCreators.GetEmployeeDocuments({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage("DOCUMENT_ADDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddEmployeeDocumentError(transformError(e)));
  }
}
export function* onGetEmployeeSpecificDocuments({
  payload,
}: Action<EmployeeEventsListRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const result = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/absence/documents`,
    );

    yield put(actionCreators.GetEmployeeSpecificDocumentsSuccess(result.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeSpecificDocumentsError(transformError(e)));
  }
}

export function* onDownloadEmployeeDocument({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  fileId: number,
  fileName: string,
}>): Generator<*, void, *> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.employeeId ||
      !payload.fileId ||
      !payload.fileName
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/file/${payload.fileId}`,
      {
        responseType: "blob",
      },
    );

    downloadFile(response.data, payload.fileName);
    yield put(actionCreators.DownloadEmployeeDocumentSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.DownloadEmployeeDocumentError(e.response));
  }
}

export function* onAddEmployeeAppeal({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  taskId: number,
  appeal: Object,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.appeal) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const data = new FormData();
    data.append(
      "appeal",
      new Blob(
        [
          JSON.stringify({
            date_received: payload.appeal.dateReceived,
            reason_for_appeal: payload.appeal.reasonForAppeal,
            additional_notes: payload.appeal.additionalNotes,
          }),
        ],
        { type: "application/json" },
      ),
    );

    const appealForm = payload.appeal.appealForm;
    if (appealForm && appealForm.startsWith("data:")) {
      const { blob, name } = dataURLtoFile(payload.appeal.appealForm);
      data.append("appealForm", blob, name);
    }

    yield call(
      http.post,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}/appeal`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(actionCreators.AddEmployeeAppealSuccess()),
      put(sendSuccessMessage("APPEAL_SUBMITTED")),
      put(
        actionCreators.GetAbsenceReviewTask({
          clientId: payload.clientId,
          taskId: payload.taskId,
        }),
      ),
      put(
        actionCreators.GetEmployeeEventHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          filters: {
            taskType: "ABSENCE_MONITORING_REVIEW",
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.AddEmployeeAppealError(transformError(e)));
  }
}

export function* onUpdateEmployeeAppeal({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  taskId: number,
  appealId: number,
  appeal: Object,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.appealId || !payload.appeal) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const data = new FormData();
    data.append(
      "appeal",
      new Blob(
        [
          JSON.stringify({
            id: payload.appeal.id,
            date_received: payload.appeal.dateReceived,
            reason_for_appeal: payload.appeal.reasonForAppeal,
            additional_notes: payload.appeal.additionalNotes,
            status: payload.appeal.status,
          }),
        ],
        { type: "application/json" },
      ),
    );

    const appealForm = payload.appeal.appealForm;
    if (appealForm && appealForm.startsWith("data:")) {
      const { blob, name } = dataURLtoFile(payload.appeal.appealForm);
      data.append("appealForm", blob, name);
    }

    // means that we want to remove currently existing file
    if (!appealForm) {
      data.append("appealForm", new Blob([], { type: "application/octet-stream" }), "null");
    }

    yield call(
      http.put,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}/appeal/${payload.appealId}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(actionCreators.UpdateEmployeeAppealSuccess()),
      put(sendSuccessMessage("APPEAL_UPDATED")),
      put(
        actionCreators.GetAbsenceReviewTask({
          clientId: payload.clientId,
          taskId: payload.taskId,
        }),
      ),
      put(
        actionCreators.GetEmployeeEventHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          filters: {
            taskType: "ABSENCE_MONITORING_REVIEW",
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeAppealError(transformError(e)));
  }
}

export function* onResolveEmployeeAppeal({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  taskId: number,
  appealId: number,
  hearing: Object,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.taskId || !payload.appealId || !payload.hearing) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { hearing } = payload;
    const data = new FormData();
    data.append(
      "appealHearing",
      new Blob(
        [
          JSON.stringify({
            hearing_date: hearing.hearingDate,
            hearing_chaired_by: hearing.hearingChairedBy,
            hr_representative: hearing.hrRepresentative,
            appeal_outcome: hearing.appealOutcome,
            rationale: hearing.rationale,
          }),
        ],
        { type: "application/json" },
      ),
    );

    const documents = hearing.documents || [];
    documents.forEach(doc => {
      if (doc && doc.startsWith("data:")) {
        const { blob, name } = dataURLtoFile(doc);
        data.append("documents", blob, name);
      }
    });

    yield call(
      http.post,
      `/api/v1/tools/client/${payload.clientId}/tools/task/absence-monitoring-review/${payload.taskId}/appeal/${payload.appealId}/resolve`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(actionCreators.ResolveEmployeeAppealSuccess()),
      put(sendSuccessMessage("APPEAL_RESOLVED")),
      put(
        actionCreators.GetAbsenceReviewTask({
          clientId: payload.clientId,
          taskId: payload.taskId,
        }),
      ),
      put(
        actionCreators.GetEmployeeEventHistory({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          filters: {
            taskType: "ABSENCE_MONITORING_REVIEW",
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.ResolveEmployeeAppealError(transformError(e)));
  }
}

export function* onEnterAbsenceMonitoringProcess({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  processId: number,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.processId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId, employeeId, processId } = payload;

    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/absence-monitoring-process/${processId}/begin`,
    );

    yield put(actionCreators.EnterAbsenceMonitoringProcessSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.EnterAbsenceMonitoringProcessError(transformError(e)));
  }
}

export function* EmployeeEventsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetEmployeeEventHistory, onGetEmployeeEventHistory),
    takeEvery(GetManualReferralEnabled, onGetManualReferralEnabled),
    takeEvery(PostManualReferral, onPostManualReferral),
    takeEvery(GetReferralProcesses, onGetReferralProcesses),
    takeEvery(GetEmployeeDocuments, onGetEmployeeDocuments),
    takeEvery(GetEmployeeSpecificDocuments, onGetEmployeeSpecificDocuments),
    takeEvery(RemoveEmployeeDocument, onRemoveEmployeeDocument),
    takeEvery(AddEmployeeDocument, onAddEmployeeDocument),
    takeEvery(DownloadEmployeeDocument, onDownloadEmployeeDocument),
    takeEvery(AddEmployeeAppeal, onAddEmployeeAppeal),
    takeEvery(UpdateEmployeeAppeal, onUpdateEmployeeAppeal),
    takeEvery(ResolveEmployeeAppeal, onResolveEmployeeAppeal),
    takeEvery(EnterAbsenceMonitoringProcess, onEnterAbsenceMonitoringProcess),
  ]);
}

export default EmployeeEventsSagas;
